/**
 * approvals-bulk.ts — Pure helpers for the Approvals inbox bulk-approve flow.
 *
 * Fix for #662 (UI cluster, fix-wave/approvals-ui-cluster):
 *   The previous handler did `Promise.all(ids.map(id => mutate(id).catch(() => null)))`
 *   and then toasted blanket success regardless of how many actually
 *   failed. That made bulk approve a trust hazard in a P1
 *   human-in-the-loop workflow.
 *
 * These helpers:
 *   1. `executeBulkApprovals` — runs the per-item mutation and
 *      partitions the result into `succeeded` (id only) vs `failed`
 *      (id + Error). Never swallows errors.
 *   2. `summarizeBulkOutcome` — produces a strongly-typed toast summary
 *      that distinguishes full success / partial failure / total
 *      failure. The UI layer reads `kind` to pick toast.success /
 *      toast.warning / toast.error and reads `title` + `description`
 *      verbatim.
 */

export interface BulkFailure {
  id: string;
  error: Error;
}

export interface BulkOutcome {
  succeeded: string[];
  failed: BulkFailure[];
}

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

export interface BulkToastSummary {
  kind: ToastKind;
  title: string;
  description?: string;
}

/**
 * Run a per-id mutation for each id and partition by success/failure.
 * Critically, this does NOT swallow errors into a generic null — it
 * surfaces the failed ids and their error objects so the caller can
 * keep them selected and tell the user which items failed.
 */
export async function executeBulkApprovals<T>(
  ids: readonly string[],
  mutate: (args: { id: string }) => Promise<T>,
): Promise<BulkOutcome> {
  if (ids.length === 0) {
    return { succeeded: [], failed: [] };
  }

  const settled = await Promise.allSettled(
    ids.map((id) => mutate({ id }).then(() => id)),
  );

  const succeeded: string[] = [];
  const failed: BulkFailure[] = [];

  settled.forEach((result, idx) => {
    const id = ids[idx];
    if (result.status === 'fulfilled') {
      succeeded.push(id);
    } else {
      const reason = result.reason;
      const error = reason instanceof Error ? reason : new Error(String(reason));
      failed.push({ id, error });
    }
  });

  return { succeeded, failed };
}

/**
 * Cap the number of ids surfaced in a toast description so we don't
 * blow past sonner's render budget on a 20-row bulk failure.
 */
const FAILED_ID_PREVIEW_LIMIT = 5;

function formatFailedIds(failed: readonly BulkFailure[]): string {
  if (failed.length <= FAILED_ID_PREVIEW_LIMIT) {
    return failed.map((f) => f.id).join(', ');
  }
  const head = failed.slice(0, FAILED_ID_PREVIEW_LIMIT).map((f) => f.id).join(', ');
  return `${head}, and ${failed.length - FAILED_ID_PREVIEW_LIMIT} more`;
}

function pluralItems(n: number): string {
  return n === 1 ? '1 item' : `${n} items`;
}

/**
 * Produce a toast summary from a BulkOutcome. The UI dispatches to
 * toast.success / toast.warning / toast.error based on `kind`.
 *
 * Invariants pinned by approvals-bulk.test.ts:
 *   - All-success → kind: 'success'
 *   - All-failure → kind: 'error'
 *   - Partial    → kind: 'warning'  (NEVER 'success' — this is the
 *                   regression we are fixing)
 *   - Empty (no items processed) → kind: 'info' (benign no-op)
 */
export function summarizeBulkOutcome(outcome: BulkOutcome): BulkToastSummary {
  const successCount = outcome.succeeded.length;
  const failCount = outcome.failed.length;

  if (successCount === 0 && failCount === 0) {
    return { kind: 'info', title: 'No approvals to process' };
  }

  if (failCount === 0) {
    return {
      kind: 'success',
      title: `Approved ${pluralItems(successCount)}`,
    };
  }

  if (successCount === 0) {
    return {
      kind: 'error',
      title: `Failed to approve ${pluralItems(failCount)}`,
      description: `Affected: ${formatFailedIds(outcome.failed)}`,
    };
  }

  // Partial — the #662 regression case. The user must see the truth.
  return {
    kind: 'warning',
    title: `Approved ${successCount}, failed ${failCount}`,
    description: `Failed: ${formatFailedIds(outcome.failed)}`,
  };
}
